import React, { useMemo } from 'react';

import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { AnyAri } from '@atlassian/ari/any-ari';

import { ConnectorSources } from '../../../../../../common/constants/schemas/3p-config';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { FederatedIcon } from '../../../../../../common/ui/federated-icon';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { serve3PClickIngestionExperiment } from '../../../../../../common/utils/experiments';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchFederatedResult_AtlassianSearchPage$key } from './__generated__/searchFederatedResult_AtlassianSearchPage.graphql';
import { SearchFederatedEmail } from './search-federated-email';

const styles = cssMap({
	typeLabelContainer: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

const getLabel = (entityNoun: string) => entityNoun.replace(/-/g, ' ');

enum ResourceType {
	EMAIL = 'email',
}

export const SearchFederatedResult = ({
	node,
	position,
	cloudId,
	primaryProduct,
	appMode,
	selectedProduct,
	searchQuery,
	searchSessionId,
	mainContentWidth,
	thirdPartyConfigs,
}: SearchResultProps<searchFederatedResult_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchFederatedResult_AtlassianSearchPage on SearchResultFederated {
				id
				lastModifiedDate
				url
				title
				description
				...searchFederatedEmail_AtlassianSearchPage
			}
		`,
		node,
	);

	const { resourceId, resourceOwner: product, resourceType } = AnyAri.parse(data.id);

	const metadata = useMemo(() => {
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: resourceType,
			entityType: resourceType,
			contentId: resourceId,
			product,
			// it's federated so always false in this component
			is3PSmartLink: false,
			connectorType: ConnectorSources.Federated,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [resourceId, product, resourceType, data.id]);


	const is3PClickIngestionEnabled = serve3PClickIngestionExperiment(thirdPartyConfigs || {}, {
		fireExperimentExposure: false,
	});
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
		is3PClickIngestionEnabled,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
		is3PClickIngestionEnabled,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});

	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const icon = <FederatedIcon product={product} resourceType={resourceType} />;

	if (resourceType === ResourceType.EMAIL) {
		return (
			<SearchFederatedEmail
				node={data}
				position={position}
				product={product}
				cloudId={cloudId}
				primaryProduct={primaryProduct}
				selectedProduct={selectedProduct}
				searchQuery={searchQuery}
				searchSessionId={searchSessionId}
				appMode={appMode}
				icon={icon}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultCtxMenuClicked}
				onCopyLinkButtonClicked={onCopyLinkButtonClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				mainContentWidth={mainContentWidth}
			/>
		);
	}

	const { id, lastModifiedDate, url, title, description } = data;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const label = getLabel(metadata.entityType);

	const headerMetadataParts = [
		['label', <Box xcss={styles.typeLabelContainer}>{label}</Box>],
		['last-modified', lastModifiedComponent],
	];

	return (
		<SearchResult
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={<DescriptionBody description={processExcerpt(description)} />}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchFederatedResult = (
	node?: any,
): node is searchFederatedResult_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultFederated';
};
